import { createPortal } from "react-dom";
import React, { useEffect, useState, Fragment, useRef } from "react";
import { PopUp } from "../../../Models";
import {  FolderIcon, TrashIcon, CheckCircleIcon, ArrowLeftCircleIcon,XCircleIcon, DocumentTextIcon } from "@heroicons/react/20/solid";
import { Transition } from '@headlessui/react'
import TagTreeItem from "./TagTreeItem";
import { PencilSquareIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import ConfirmModal from "../ConfirmModal";
import { NIL } from "uuid";
import { deleteSubtag, deleteTag, editSubtag, editTag, getTags, PostSubtag, postTag } from "../../../api/tag.api";
import useApiToken from "../../../hooks/useApiToken";

interface Props {
  isOpen: boolean;
  onCancel: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  tab: string;
  refreshTags: () => void;
  setNotificationPopUp: (notificationObject: PopUp) => void;
  setShow: (show: boolean) => void;
  getData: (obj: any) => void;
  rankType?: string;
  view: string;
}

interface Subtag {
  id: string;
  name: string;
  count: number;
}

interface Tag {
  id: string;
  name: string;
  count: number;
  subtags: Subtag[]
}

const TagManagementModal = (props: Props) => {
  const token = useApiToken();
  const [tags, setTags] = useState<Tag[]>([]);

  const [hoveredItem, setHoveredItem ] = useState<string>("");
  const [newItem, setNewItem] = useState<boolean>(false);
  const [level, setLevel] = useState<number>(0);
  const [inputText, setInputText] = useState<string>("");
  const [parentTag, setParentTag] = useState<Tag | null>(null);
  const [deleteItemId, setDeleteItemId] = useState<string>("");
  const [deleteRankTypeModal, setDeleteRankTypeModal] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<{ id: string, editItemName: string}>({ id: "", editItemName: ""});
  const [editableFlag, setEditableFlag] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; 
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px"; 
    }
  }, [inputText]);

  useEffect(() => {
    if (props.isOpen) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "auto";
    }
    return () => {
      document.body.style.overflowY = "auto";
    };

  }, [props.isOpen]);

  const cleanUp = () => {
    setLevel(0);
    setInputText("");
    setParentTag(null);
    setNewItem(false);
  }

  useEffect(() => {
    cleanUp();
    getTagList();
  }, [props.rankType, props.tab, props.isOpen, token]);


  const getTagList = async () => {
    if(props.view === "" || !token) return;
    const res = await getTags(props.view === "quarantine" ? NIL : props.rankType, props.tab === "Companies", props.view, token);
    if(res === undefined){
      return;
    }
    const tagsCombobox = res.map((tag:any) => {
                            return { id: tag.id, name: tag.name, count: tag.count, subtags: [...tag.subtags]}
                        });

    setTags(tagsCombobox);

    if(parentTag !== null){
      //@ts-ignore
      setParentTag((prev: Tag) => {
        const parent = tagsCombobox.find((t: Tag) => {
          return t.id === parentTag.id;
        });

        return { ...prev, subtags: parent.subtags }
      });

    }
  }


  const handlePostTag = async () =>
  {
    if(inputText === "" || !token) {
      props.setNotificationPopUp({
        title: "Failed",
        description: "Updated label can't be empty.",
        type: "error"
      });
      props.setShow(true);
      return;
    }
    try{
      let res;
      if(props.tab === "Companies"){
          res = await postTag(inputText, props.view === "quarantine" ? NIL : props.rankType, props.view, true, token);
      }
      else{
          res = await postTag(inputText, props.view === "quarantine" ? NIL : props.rankType, props.view, false, token);
      }

      if(res && res.error){
          props.setNotificationPopUp({
          title: "Failed",
          description: res.message,
          type: "error"
          });
      }
      else{
          props.setNotificationPopUp({
          title: "Success",
          description: "Tag is successfully created.",
          type: "success"
          });
      }
      setInputText("");
      props.setShow(true);
      props.refreshTags();
      setNewItem(false);
      getTagList();
    }
    catch(ex){
      console.log(ex);
    }
  }
  
  const handlePostSubtag = async () => {
    if(parentTag === null || inputText === "" || !token) {
      setNewItem(false);
      props.setNotificationPopUp({
        title: "Failed",
        description: "Updated label can't be empty.",
        type: "error"
      });
      props.setShow(true);
      return;
    }
    const res = await PostSubtag(parentTag.id, inputText, token);

    if(res && res.error){
      props.setNotificationPopUp({
      title: "Failed",
      description: res.message,
      type: "error"
      });
    }
    else{
        props.setNotificationPopUp({
        title: "Success",
        description: "Subtag is successfully added to the tag.",
        type: "success"
        });
    }
    props.setShow(true);
    setNewItem(false);
    setInputText('');
    props.refreshTags();
    getTagList();
  }

  const handleDeleteItem = async () => {
    if (!token) return;
    let res;
    if(level === 0){
      res = await deleteTag(deleteItemId, token);
    }
    else{
      if(parentTag == null) return;
      res = await deleteSubtag(parentTag.id, deleteItemId, token);
    }

    if(res && res.error){
      props.setNotificationPopUp({
      title: "Failed",
      description: res.message,
      type: "error"
      });
    }
    else{
        props.setNotificationPopUp({
        title: "Success",
        description: "Item is deleted successfully.",
        type: "success"
        });
        getTagList();
    }
    props.setShow(true);
    props.getData({pageChange: false, reload: true});
    props.refreshTags();
    setDeleteRankTypeModal(false);
  }

  const handleEditItem = async () => {
    if(editItem.editItemName === "" || !token){
      setEditItem({id: "", editItemName: ""});
      setEditableFlag(false);
      props.setNotificationPopUp({
        title: "Failed",
        description: "Updated label can't be empty.",
        type: "error"
      });
      props.setShow(true);
      return;
    }

    let data;
    if(level === 0){
      data = await editTag(editItem.id, editItem.editItemName, token);
      if(data && data.error === false){
        const updatedTags = tags.map((t) => {
          if(editItem.id === t.id){
           return {...t, name: editItem.editItemName};
          } 
          return t;
        });
  
        setTags(updatedTags);
      }
      else{

        var duplicatedSubtag = parentTag?.subtags.some((st) => st.name === editItem.editItemName);
        if(duplicatedSubtag){
          props.setNotificationPopUp({
            title: "Failed",
            description: "There is already a subtag with that name.",
            type: "error"
          });
          setEditableFlag(false);
          props.setShow(true);
          setEditItem({id:"", editItemName: ""});
          return;
        }
        data = await editSubtag(editItem.id, editItem.editItemName, token);
        if(data && data.error === false){
          if(parentTag !== null ){
            parentTag.subtags.map((st) => {
              if(st.id === editItem.id){
                st.name = editItem.editItemName;
              }
              return st;
            })
            setParentTag(parentTag);
          }
        }

      }



      props.getData({pageChange: false, reload: true});
      props.refreshTags();
      setEditableFlag(false);
      setEditItem({id:"", editItemName: ""});
    }
    else{
      data = await editSubtag(editItem.id, editItem.editItemName, token);
      if(parentTag !== null ){
        parentTag.subtags.map((st) => {
          if(st.id === editItem.id){
            st.name = editItem.editItemName;
          }
          return st;
        })
        setParentTag(parentTag);
      }

    }
    if(data && data.error){
      props.setNotificationPopUp({
      title: "Failed",
      description: data.message,
      type: "error"
      });
    }
    else{
        props.setNotificationPopUp({
        title: "Success",
        description: "Item is successfully edited.",
        type: "success"
        });
        getTagList();
    }
    props.getData({pageChange: false, reload: true});
    props.refreshTags();
    setEditableFlag(false);
    props.setShow(true);
    setEditItem({id:"", editItemName: ""});
  }

  return createPortal(
  <>
    <Transition.Root show={props.isOpen} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <div
          className={`${
            props.isOpen ? "transition-all" : "hidden"
          } fixed top-0 right-0 left-0 bottom-0  w-[100vw] z-20`}
        >
          <div
            className="absolute bg-gray-400 opacity-40 top-0 w-full h-screen z-10"
            onClick={() => { 
              cleanUp();
              props.onCancel(false)
            }}
          ></div>
          <div
            className={`absolute top-0 right-0 bottom-[-40px] left-0 max-w-[1100px] max-h-max m-auto z-50 p-4`}
          >
            <header className="  bg-gray-100 dark:bg-gray-700 rounded-t-xl p-2 text-center text-gray-700 dark:text-gray-400 font-medium ">
              {props.title}
            </header>
            
            <section className="flex bg-white text-sm dark:bg-darkbglight overflow-auto gap-4 rounded-b-xl overflow-y-scroll">
            <aside className="w-[200px] p-10 py-8 pt-4 max-h-[500px] pr-0 border-r-2 border-gray-400">
              <div className="w-full h-full pt-14 overflow-y-scroll pr-0">
               {
                  tags.map((tag ) => {
                    return (
                      <TagTreeItem key={tag.id} tag={tag}/>
                    )
                  })
                }
              </div>
            </aside>
            <main className="flex flex-col flex-1 bg-white items-center text-sm justify-center dark:bg-darkbglight p-16  gap-4 py-8 pl-0 pt-4">
              <div className="self-start flex items-center">
                { 
                  level > 0 && 
                  <ArrowLeftCircleIcon 
                  className="h-6 w-6 mr-3 dark:text-gray-300 cursor-pointer"
                  onClick={ () => {
                    setLevel(0);
                    setParentTag(null);
                    setNewItem(false);
                    setInputText("");
                    setEditableFlag(false);
                        setEditItem({id:"", editItemName: ""});
                  }}
                  /> 
                }
                <button 
                className="inline-flex gap-2 items-center rounded-md bg-entntblue px-3 py-1 
                 pl-2 text-sm font-semibold text-white shadow-sm hover:bg-entntorange focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:hover:bg-entntblue disabled:cursor-not-allowed"
                onClick={() => { 
                  setInputText(""); 
                  setNewItem(true);
                }} 
                >
                  <PlusCircleIcon className="h-5 w-5"/>
                  New
                </button>
              </div>
              <div className="border rounded-lg h-[50vh] overflow-y-scroll w-full flex flex-wrap px-12 py-8 gap-6
              ">
                {
                  level === 0 && tags.map((t) => {
                    return (
                      <div 
                      className="flex relative justify-center h-max cursor-pointer mr-2"
                      onMouseEnter={() => setHoveredItem(t.id)}
                      onMouseLeave={() => setHoveredItem("")}
                      onClick={() => {
                        setNewItem(false);
                        setInputText("");
                        setLevel(1);
                        setParentTag(t);
                        setEditableFlag(false);
                        setEditItem({id:"", editItemName: ""});
                      }}
                      key={t.id}
                      > 
                        <div 
                        className="text-xs px-1 text-black dark:text-gray-300 border rounded-full border-gray-500 max-h-min leading-[12px]"
                        onClick={(e) => e.stopPropagation()}
                        >
                          {t.count}
                        </div>  
                        <div className="text-center relative">
                          <FolderIcon className="h-14 w-14 text-yellow-600"/> 
                          { 
                              editableFlag && editItem.id === t.id ? 
                              <div className="absolute flex gap-2 ">
                                <textarea
                                  ref={textareaRef}
                                  value={editItem.editItemName}
                                  onChange={(e) => setEditItem((prev) => ({...prev, editItemName: e.target.value}))}
                                  placeholder={`${level === 0 ? "tag" : "subtag"}`}
                                  rows={1}
                                  style={{
                                    maxWidth: "3rem",
                                    overflow: "hidden", 
                                    resize: "none",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  className="text-xs p-0 outline-none border-none h-8 focus:ring-0"
                                />
                                <div className="flex ">
                                  <CheckCircleIcon 
                                  className="h-4 w-4 hover:text-gray-300 dark:text-gray-300"
                                  onClick={ (e) =>  {
                                    e.stopPropagation(); 
                                    handleEditItem();
                                    
                                  }}
                                  />
                                  <XCircleIcon 
                                  className="h-4 w-4 text-red-600 dark:text-red-400"
                                  onClick={(e) =>  {
                                    e.stopPropagation(); 
                                    setEditableFlag(false);
                                    setEditItem({id:"", editItemName: ""});
                                  }}
                                  />
                                </div>
                              </div>
                            : <span className="text-xs text-black dark:text-gray-300">{t.name}</span>
                          }
                        </div>
                        {
                          (hoveredItem === t.id) && 
                          <div className = "absolute -right-2">
                            <div className = "flex flex-col py-2 pt-3">
                              <PencilSquareIcon 
                              className="h-3 w-3 mb-[12px] hover:text-gray-400 dark:text-gray-300"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 setEditableFlag(true); 
                                 setEditItem((prev) => ({...prev, id:t.id}))                        
                              }}
                              />
                              <TrashIcon 
                              className="h-3 w-3 text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteRankTypeModal(true);
                                setDeleteItemId(t.id);
                              }}
                              />
                            </div>
                          </div>   
                        }
                      </div>
                    );
                  })
                }

                {
                  level === 1 && (parentTag && parentTag.subtags.length > 0 ? parentTag.subtags.map((st) => {
                    return (
                      <div 
                      className="flex relative justify-center h-max cursor-pointer mr-2"
                      onMouseEnter={() => setHoveredItem(st.id)}
                      onMouseLeave={() => setHoveredItem("")}
                      key={st.id}
                      >  
                        <div 
                        className="text-xs px-1 text-black dark:text-gray-300 border rounded-full border-gray-500 max-h-min leading-[12px]"
                        onClick={(e) => e.stopPropagation()}
                        >
                          {st.count}
                        </div>   
                        <div className="relative text-center">
                          <DocumentTextIcon className="h-14 w-14 text-gray-600"/> 
                          { 
                              editableFlag && editItem.id === st.id ? 
                              <div className="flex gap-2 absolute">
                                <textarea
                                  ref={textareaRef}
                                  value={editItem.editItemName}
                                  onChange={(e) => setEditItem((prev) => ({...prev, editItemName: e.target.value}))}
                                  placeholder="subtag"
                                  rows={1}
                                  style={{
                                    maxWidth: "3rem",
                                    overflow: "hidden",
                                    resize: "none",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  className="text-xs p-0 outline-none border-none h-8 focus:ring-0"
                                />
                                <div className="flex ">
                                  <CheckCircleIcon 
                                  className="h-4 w-4 hover:text-gray-300 dark:text-gray-300"
                                  onClick={ (e) =>  {
                                    e.stopPropagation(); 
                                    handleEditItem();
                                  }}
                                  />
                                  <XCircleIcon 
                                  className="h-4 w-4 text-red-600 dark:text-red-400"
                                  onClick={(e) =>  {
                                    e.stopPropagation(); 
                                    setEditableFlag(false);
                                    setEditItem({id:"", editItemName: ""});
                                  }}
                                  />
                                </div>
                              </div>
                            : <span className="text-xs dark:text-gray-300 text-black">{st.name}</span>
                          }
                        </div>
                        {
                          (hoveredItem === st.id) && 
                          <div className = "absolute -right-2">
                            <div className = "flex flex-col py-2 pt-3">
                              <PencilSquareIcon 
                              className="h-3 w-3 mb-[12px] hover:text-gray-400 dark:text-gray-300"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 setEditableFlag(true); 
                                 setEditItem((prev) => ({...prev, id:st.id}))                        
                              }}
                              />
                              <TrashIcon 
                              className="h-3 w-3 text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteRankTypeModal(true);
                                setDeleteItemId(st.id);
                              }}
                              />
                            </div>
                          </div>   
                        }
                      </div>
                    );
                  }
                ):
                newItem ? <div></div> : <div className="m-auto dark:text-gray-300 text-black">No subtags found for this tag.</div>
                )}

                {
                  newItem && (
                    <div 
                    className="flex relative justify-center h-max cursor-pointer gap-2"
                    >   
                      <div className="text-center">
                      { level === 0 ? <FolderIcon className="h-14 w-14 text-yellow-600"/> : <DocumentTextIcon className="h-14 w-14 text-gray-600"/> }
                        <div className="flex gap-2 ">

                          {/* <input type="text" name="folderName" placeholder="tag" className="h-full text-sm max-w-min "/> */}
                          <textarea
                            ref={textareaRef}
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder={`${level === 0 ? "tag" : "subtag"}`}
                            rows={1}
                            style={{
                              maxWidth: "3rem",
                              overflow: "hidden",
                              resize: "none",
                            }}
                            autoFocus
                            className="text-xs p-0 outline-none border-none h-8 focus:ring-0"
                          />
                          <div className="flex ">
                            <CheckCircleIcon 
                            className="h-4 w-4 hover:text-gray-300 dark:text-gray-300"
                            onClick={level === 0 ? handlePostTag : handlePostSubtag}
                            />
                            <XCircleIcon 
                            className="h-4 w-4 text-red-600 dark:text-red-400"
                            onClick={() => setNewItem(false)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>  
                  )
                }
              </div>
              </main>
            </section>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>
    <ConfirmModal
    open={deleteRankTypeModal}
    setOpen={setDeleteRankTypeModal}
    onConfirm={ handleDeleteItem}
    type="danger"
    title="Delete item"
    description="Are you sure?"
    />
    </>
    ,
    document.body
  );
};

export default TagManagementModal;
                                                                                                                                               